import React, { useState, useEffect, useRef } from "react";
import { Typography, Grid, Paper, IconButton, Tabs, Tab, Box, ToggleButtonGroup, ToggleButton, FormControl, InputLabel, Select, MenuItem, Chip, Alert, Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  createPatientAction,
  updatePatientAction,
} from "../../redux/actions/patient";
import CustomTextField from "../../form/CustomTextField";
import CustomSelectField from "../../form/CustomSelectField";
import CustomAutocompleteField from "../../form/CustomAutocompleteField";
import CustomDateField from "../../form/CustomDateField";
import strings from "../../i18n/language";
import Fab from "@mui/material/Fab";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import dayjs from "dayjs";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { instance } from "../../axios";
import AutoFixNormalIcon from "@mui/icons-material/AutoFixNormal";
import InfoIcon from "@mui/icons-material/Info";
import AntecedentesFamiliares from "./AntecedentesFamiliares";
import AntecedentesPersonales from "./AntecedentesPersonales";

const isMoreThan18Years = (birthDate) => {
  const now = dayjs();
  const age = now.diff(dayjs(birthDate), "year");
  return age >= 18;
};

const PatientEdit = () => {
  // State for editable fields
  const navigate = useNavigate();
  const location = useLocation();
  const [displayName, setDiplayName] = useState(null);
  const [phone, setPhone] = useState(null);
  const [birthDate, setBirthDate] = useState(null);
  const [startContract, setStartContract] = useState(null);
  const [mail, setMail] = useState(null);
  const [gender, setGender] = useState(null);
  const [head, setHead] = useState(true);
  const [type, setType] = useState(null);
  const [bloodType, setBloodType] = useState(null);
  const [address, setAddress] = useState(null);
  const [legalRepresentant, setLegalRepresentant] = useState(null);
  const [comment, setComment] = useState(null);
  const [errorDisplayName, setErrorDisplayName] = useState(false);
  const [identityNumber, setIdentityNumber] = useState(null);
  const [errorIdentityNumber, setErrorIdentityNumber] = useState(false);
  const [duplicatePatient, setDuplicatePatient] = useState(null);
  const [checkingIdentity, setCheckingIdentity] = useState(false);
  const [errorBirthDate, setErrorBirthDate] = useState(false);
  const [errorType, setErrorType] = useState(false);
  const [showRepresentant, setShowRepresentant] = useState(false);
  const [initialState, setInitialState] = useState({});

  const [estadoCivil, setEstadoCivil] = useState(null);
  const [lugarNacimiento, setLugarNacimiento] = useState(null);
  const [nacionalidad, setNacionalidad] = useState(null);
  const [parroquia, setParroquia] = useState(null);
  const [municipio, setMunicipio] = useState(null);
  const [estadoResidencia, setEstadoResidencia] = useState(null);
  const [nivelEducativo, setNivelEducativo] = useState(null);
  const [manoDominante, setManoDominante] = useState(null);
  const [discapacitado, setDiscapacitado] = useState(null);
  const [modules, setModules] = useState([]);

  const [valid, setValid] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState(0);
  const antFamRef = useRef();
  const antPerRef = useRef();
  const patient = useSelector((state) => state.patient.patient);
  const dispatch = useDispatch();

  useEffect(() => {
    return setDirty(
      displayName !== initialState.displayName ||
        phone !== initialState.phone ||
        mail !== initialState.mail ||
        comment !== initialState.comment ||
        head !== initialState.head ||
        gender !== initialState.gender ||
        bloodType !== initialState.bloodType ||
        address !== initialState.address ||
        identityNumber !== initialState.identityNumber ||
        !dayjs(birthDate).isSame(initialState.birthDate) ||
        legalRepresentant?.id !== initialState.legalRepresentant?.id ||
        type !== initialState.type
    );
  }, [
    displayName,
    phone,
    mail,
    comment,
    head,
    gender,
    bloodType,
    address,
    identityNumber,
    birthDate,
    type,
    legalRepresentant,
    initialState,
  ]);

  useEffect(() => {
    if (type !== "family") {
      setHead(true);
    }
    if (!type) setErrorType(true);
    else setErrorType(false);
  }, [type]);

  useEffect(() => {
    if (!patient && location.state?.displayName) {
      setDiplayName(location.state.displayName);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (patient) {
      setDiplayName(patient.displayName);
      setPhone(patient.phone);
      setMail(patient.mail);
      setComment(patient.comment);
      setHead(patient.head);
      setGender(patient.gender);
      setBloodType(patient.bloodType);
      setAddress(patient.address);
      setIdentityNumber(patient.identityNumber);
      if (patient.birthDate) setBirthDate(dayjs(patient.birthDate));
      if (patient.Parent) setLegalRepresentant(patient.Parent);
      if (patient.type) setType(patient.type);
      setEstadoCivil(patient.estadoCivil || null);
      setLugarNacimiento(patient.lugarNacimiento || null);
      setNacionalidad(patient.nacionalidad || null);
      setParroquia(patient.parroquia || null);
      setMunicipio(patient.municipio || null);
      setEstadoResidencia(patient.estadoResidencia || null);
      setNivelEducativo(patient.nivelEducativo || null);
      setManoDominante(patient.manoDominante || null);
      setDiscapacitado(patient.discapacitado ?? null);
      const mods = patient.modules;
      setModules(Array.isArray(mods) ? mods : (typeof mods === "string" ? (() => { try { return JSON.parse(mods); } catch { return []; } })() : []));
      setInitialState(patient);
    }
  }, [patient, dispatch]);

  useEffect(() => {
    if (errorDisplayName || errorBirthDate || errorIdentityNumber) {
      setValid(false);
    } else {
      setValid(true);
    }
  }, [errorDisplayName, errorBirthDate, errorIdentityNumber, errorType, patient]);

  useEffect(() => {
    setDuplicatePatient(null);
    const isValid = /^[VE]-\d{5,8}$/.test(identityNumber || "");
    setErrorIdentityNumber(!!identityNumber && !isValid);
    setCheckingIdentity(isValid);
    if (!isValid) return;
    const timer = setTimeout(async () => {
      try {
        const excludeId = patient?.id ? `?excludeId=${patient.id}` : "";
        const res = await instance.get(`/patient/check-identity/${encodeURIComponent(identityNumber)}${excludeId}`);
        setDuplicatePatient(res.data || null);
      } catch (_) {
      } finally {
        setCheckingIdentity(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [identityNumber, patient?.id]);

  useEffect(() => {
    const alphanumericRegex = /^[a-z0-9\sáéíóúñüäöüß.,;:!?'"\-()%]+$/i;
    if (displayName && displayName.length > 4 && displayName.length < 60) {
      if (alphanumericRegex.test(displayName)) {
        setErrorDisplayName(false);
      } else {
        setErrorDisplayName(true);
      }
    } else setErrorDisplayName(true);
  }, [displayName]);

  useEffect(() => {
    const now = dayjs();
    if (
      !birthDate ||
      !dayjs(birthDate).isValid() ||
      dayjs(birthDate).isAfter(now)
    ) {
      setErrorBirthDate(true);
    } else {
      setShowRepresentant(!isMoreThan18Years(birthDate));
      setErrorBirthDate(false);
    }
  }, [birthDate]);


  // Cédula obligatoire à la création pour les majeurs ; une cédula déjà enregistrée bloque l'enregistrement
  const missingIdentity = !patient?.id && !showRepresentant && !identityNumber;
  const blockingDuplicate = !!duplicatePatient && identityNumber !== initialState.identityNumber;

  const save = async () => {
    const antFam = antFamRef.current?.getPayload() || null;
    const antPer = antPerRef.current?.getPayload() || null;
    try {
      if (patient && patient.id)
        await dispatch(
          updatePatientAction({
            displayName: displayName,
            identityNumber: identityNumber,
            gender: gender,
            birthDate: birthDate,
            bloodType: bloodType,
            address: address,
            phone: phone,
            mail: mail,
            comment: comment,
            type: type,
            head: head,
            startContract: startContract,
            parent_id: legalRepresentant?.id,
            id: patient.id,
            antecedentesFamiliares: antFam,
            antecedentesPersonales: antPer,
            estadoCivil, lugarNacimiento, nacionalidad,
            parroquia, municipio, estadoResidencia,
            nivelEducativo, manoDominante, modules, discapacitado,
          })
        );
      else
        await dispatch(
          createPatientAction({
            displayName: displayName,
            identityNumber: identityNumber,
            phone: phone,
            mail: mail,
            gender: gender,
            birthDate: birthDate,
            bloodType: bloodType,
            address: address,
            type: type,
            startContract: startContract,
            head: head,
            parent_id: legalRepresentant?.id,
            comment: comment,
            antecedentesFamiliares: antFam,
            antecedentesPersonales: antPer,
            estadoCivil, lugarNacimiento, nacionalidad,
            parroquia, municipio, estadoResidencia,
            nivelEducativo, manoDominante, modules, discapacitado,
          })
        );
    } catch (error) {
      // Cédula déjà enregistrée (contrôle côté API) : rester sur le formulaire
      if (error?.response?.status === 409) {
        setDuplicatePatient(error.response.data.patient);
        return;
      }
    }
    navigate(patient?.id ? `/patients/${patient.id}` : "/patients");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => navigate(-1)} sx={{ alignSelf: "flex-start", mb: 1, color: "text.secondary" }}>
        Volver
      </Button>
      <Paper
        sx={{
          width: "80%",
          backgroundColor: "white",
        }}
      >
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}>
          <Tab label="Información general" />
          <Tab label="Datos personales" />
          <Tab label="Antecedentes familiares" />
          <Tab label="Antecedentes personales" />
        </Tabs>

        {/* ── Tab 1 : Datos personales ── */}
        <Box sx={{ p: 3, display: tab === 1 ? "block" : "none" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado civil</InputLabel>
                <Select value={estadoCivil || ""} label="Estado civil" onChange={(e) => setEstadoCivil(e.target.value || null)}>
                  <MenuItem value=""><em>—</em></MenuItem>
                  <MenuItem value="soltero">Soltero/a (S)</MenuItem>
                  <MenuItem value="casado">Casado/a (C)</MenuItem>
                  <MenuItem value="divorciado">Divorciado/a (D)</MenuItem>
                  <MenuItem value="viudo">Viudo/a (V)</MenuItem>
                  <MenuItem value="concubinato">Concubinato (Co)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField fullWidth label="Nacionalidad" value={nacionalidad} onChange={setNacionalidad} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField fullWidth label="Lugar de nacimiento" value={lugarNacimiento} onChange={setLugarNacimiento} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField fullWidth label="Parroquia" value={parroquia} onChange={setParroquia} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField fullWidth label="Municipio" value={municipio} onChange={setMunicipio} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField fullWidth label="Estado" value={estadoResidencia} onChange={setEstadoResidencia} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>Nivel educativo</Typography>
              <ToggleButtonGroup
                value={nivelEducativo}
                exclusive
                onChange={(e, v) => setNivelEducativo(v)}
                size="small"
              >
                <ToggleButton value="iletrado">Iletrado</ToggleButton>
                <ToggleButton value="primaria">Primaria</ToggleButton>
                <ToggleButton value="tecnica">Técnica</ToggleButton>
                <ToggleButton value="secundaria">Secundaria</ToggleButton>
                <ToggleButton value="universitaria">Universitaria</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>Mano dominante</Typography>
              <ToggleButtonGroup
                value={manoDominante}
                exclusive
                onChange={(e, v) => setManoDominante(v)}
                size="small"
              >
                <ToggleButton value="izquierda">Izquierda</ToggleButton>
                <ToggleButton value="derecha">Derecha</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!discapacitado}
                    onChange={(e) => setDiscapacitado(e.target.checked || null)}
                  />
                }
                label="Paciente discapacitado"
              />
            </Grid>
          </Grid>
        </Box>

        {/* ── Tab 2 : Antecedentes familiares ── */}
        <Box sx={{ p: 3, display: tab === 2 ? "block" : "none" }}>
          <AntecedentesFamiliares ref={antFamRef} patient={patient} editing={true} />
        </Box>

        {/* ── Tab 3 : Antecedentes personales ── */}
        <Box sx={{ p: 3, display: tab === 3 ? "block" : "none" }}>
          <AntecedentesPersonales ref={antPerRef} patient={patient} editing={true} />
        </Box>

        {/* ── Tab 0 : Información general ── */}
        <Grid container justifyContent="center" margin="normal" mt={2} sx={{ p: 2, display: tab === 0 ? "flex" : "none" }}>
          <Grid item xs={8} mt={2} display="flex">
            <CustomTextField
              margin="normal"
              fullWidth
              id="name"
              label={strings.LABEL_NAME}
              name="name"
              required
              value={displayName}
              onChange={setDiplayName}
              autoFocus
            />
          </Grid>
          {displayName && (
            <Grid item xs={8} mt={1}>
              <Typography color={errorDisplayName ? "error" : "success.main"}>
                {strings.REQUIREMENT_NAME_PATIENT}
              </Typography>
            </Grid>
          )}
          <Grid item xs={8} mt={2} display="flex">
            <CustomTextField
              margin="normal"
              fullWidth
              id="name"
              label={"Cédula"}
              name="name"
              required={!patient?.id && !showRepresentant}
              value={identityNumber}
              onChange={setIdentityNumber}
              autoFocus
              error={errorIdentityNumber}
              helperText={
                errorIdentityNumber
                  ? "Formato incorrecto. Ej: V-12345678"
                  : missingIdentity && birthDate
                  ? "Obligatoria para pacientes mayores de edad"
                  : ""
              }
            />
          </Grid>
          {duplicatePatient && (
            <Grid item xs={8}>
              <Alert
                severity={blockingDuplicate ? "error" : "warning"}
                action={
                  <Box
                    component="span"
                    sx={{ cursor: "pointer", textDecoration: "underline", fontWeight: "bold", whiteSpace: "nowrap" }}
                    onClick={() => navigate(`/patients/${duplicatePatient.id}`)}
                  >
                    Ver ficha
                  </Box>
                }
              >
                Esta cédula ya existe: <strong>{duplicatePatient.displayName}</strong>
                {blockingDuplicate && ". No se puede registrar otro paciente con la misma cédula."}
              </Alert>
            </Grid>
          )}
          <Grid item xs={8} mt={1} display="flex" alignItems="center">
            <InfoIcon style={{ marginRight: 8 }} />
            <Typography>{strings.REQUIREMENT_ID_PATIENT}</Typography>
          </Grid>
          <Grid item xs={8} mt={2} display="flex">
            <CustomSelectField
              onChange={setGender}
              margin="normal"
              title={strings.LABEL_GENDER}
              id={"gender"}
              value={gender}
              defaultValues={[
                { value: "man", label: strings.LABEL_MAN },
                { value: "woman", label: strings.LABEL_WOMAN },
              ]}
              defaultValue={"man"}
            ></CustomSelectField>
          </Grid>
          <Grid item xs={8} mt={2} display="flex">
            <CustomSelectField
              onChange={setBloodType}
              margin="normal"
              title={strings.LABEL_BLOOD_TYPE}
              id={"blood-type"}
              value={bloodType}
              defaultValues={[
                { value: "A-", label: "A-" },
                { value: "A+", label: "A+" },
                { value: "B-", label: "B-" },
                { value: "B+", label: "B+" },
                { value: "AB-", label: "AB-" },
                { value: "AB+", label: "AB+" },
                { value: "O-", label: "O-" },
                { value: "O+", label: "O+" },
              ]}
              defaultValue={"A-"}
            ></CustomSelectField>
          </Grid>
          <Grid item xs={8} mt={2} display="flex">
            <CustomDateField
              value={birthDate}
              required
              onChange={setBirthDate}
              margin="normal"
              isValid={errorBirthDate}
              label={strings.LABEL_BIRTH_DATE}
            />
          </Grid>
          {birthDate && (
            <Grid item xs={8} mt={1}>
              <Typography color={errorBirthDate ? "error" : "success.main"}>
                {strings.REQUIREMENT_BIRTH_DATE}
              </Typography>
            </Grid>
          )}
          {showRepresentant && (
            <>
              <Grid item xs={8} mt={2} display="flex">
                <CustomAutocompleteField
                  loading={true}
                  value={legalRepresentant}
                  setValue={setLegalRepresentant}
                  onChange={setLegalRepresentant}
                  labelField={"displayName"}
                  margin="normal"
                  noOptionsText={strings.MESSAGE_NO_RESULTS}
                  label={strings.LABEL_LEGAL_REPRESENTATIVE}
                  launch={async (inputValue, callback) => {
                    const patients = await instance.get(
                      `/patient?query=${encodeURI(inputValue.toLowerCase())}`
                    );
                    callback(patients.data?.data);
                    return patients.data?.data;
                  }}
                ></CustomAutocompleteField>
              </Grid>
              <Grid item xs={8} mt={1}>
                <Typography
                  color={!legalRepresentant ? "error" : "success.main"}
                >
                  {strings.REQUIREMENT_MINOR}
                </Typography>
              </Grid>
            </>
          )}

          <Grid item xs={8} mt={2} display="flex">
            <CustomTextField
              margin="normal"
              fullWidth
              id="phone"
              label={strings.LABEL_PHONE}
              name="phone"
              value={phone}
              onChange={setPhone}
              autoFocus
            />
          </Grid>
          <Grid item xs={8} mt={2} display="flex">
            <CustomTextField
              margin="normal"
              fullWidth
              id="address"
              label={strings.LABEL_ADDRESS}
              name="address"
              value={address}
              onChange={setAddress}
              autoFocus
            />
          </Grid>
          <Grid item xs={8} mt={2} display="flex">
            <CustomTextField
              margin="normal"
              fullWidth
              id="mail"
              label={strings.LABEL_MAIL}
              name="mail"
              value={mail}
              onChange={setMail}
              autoFocus
            />
          </Grid>
          <Grid item xs={8} mt={2} display="flex">
            <CustomTextField
              margin="normal"
              fullWidth
              id="comment"
              label={strings.LABEL_COMMENT}
              name="comment"
              value={comment}
              onChange={setComment}
              autoFocus
            />
          </Grid>
          <Grid item xs={8} mt={2}>
            <Typography variant="body2" color="text.secondary" mb={1}>Modules</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {["Servital", "Laboral"].map((mod) => {
                const active = modules.includes(mod);
                return (
                  <Chip
                    key={mod}
                    label={mod}
                    color={active ? "primary" : "default"}
                    variant={active ? "filled" : "outlined"}
                    onClick={() => setModules(active ? modules.filter((m) => m !== mod) : [...modules, mod])}
                    clickable
                  />
                );
              })}
            </Box>
          </Grid>
          <Grid item xs={8} mt={2} display="flex">
            <CustomSelectField
              onChange={setType}
              margin="normal"
              title={strings.LABEL_SUBSCRIPTION}
              id={"type"}
              value={type}
              defaultValues={[
                { value: "individual", label: strings.LABEL_INDIVIDUAL },
                { value: "family", label: strings.LABEL_FAMILY_GROUP },
                { value: "company", label: strings.LABEL_COMPANY },
              ]}
              defaultValue={"individual"}
            ></CustomSelectField>
          </Grid>
          {type === "family" && (
            <>
              <Grid item xs={8} mt={2} display="flex">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={head}
                      value={head}
                      onChange={() => {
                        setHead(!head);
                      }}
                    />
                  }
                  label={strings.MESSAGE_HEAD_FAMILY_GROUP}
                />
              </Grid>
            </>
          )}
          {(type === "company" || (type === "family" && !head)) && (
            <>
              <Grid item xs={8} mt={1} display="flex" alignItems="center">
                <InfoIcon style={{ marginRight: 8 }} />
                <Typography>
                  {strings.MESSAGE_INTEGRATE_PATIENT_IN_SUBSCRIPTION}
                </Typography>
              </Grid>
            </>
          )}
          {(!patient?.id || (patient?.id && !patient?.PatientSubscriptions?.length)) &&
            !(!head && type === "family") &&
            type &&
            type !== "company" && (
              <>
                <Grid item xs={8} display="flex">
                  <IconButton
                    onClick={() => {
                      setStartContract(dayjs(new Date()));
                    }}
                  >
                    <AutoFixNormalIcon></AutoFixNormalIcon>
                  </IconButton>
                  <CustomDateField
                    value={startContract}
                    onChange={setStartContract}
                    required
                    margin="normal"
                    isValid={!!startContract}
                    label={strings.LABEL_DATE_START_CONTRACT}
                  />
                </Grid>
                <Grid item xs={8} mt={1} display="flex" alignItems="center">
                  <InfoIcon style={{ marginRight: 8 }} />
                  <Typography>
                    {strings.MESSAGE_SUBSCRIPTION_WILL_CREATE_AUTOMATICALLY}
                  </Typography>
                </Grid>
              </>
            )}
        </Grid>
      </Paper>
      <div
        style={{
          alignSelf: "flex-end",
          flexDirection: "column",
          display: "flex",
          marginLeft: 10,
        }}
      >
        <Fab color="error" aria-label="cancel" disabled={true}>
          <CancelIcon />
        </Fab>
        <Fab
          color="primary"
          aria-label="add"
          style={{
            marginTop: 10,
          }}
          onClick={save}
          disabled={!(dirty && valid) || missingIdentity || blockingDuplicate || checkingIdentity}
        >
          <SaveIcon />
        </Fab>
      </div>
    </Box>
  );
};

export default PatientEdit;
